import * as React from "react";
import { WizardState, WizardActionTypes } from "../WizardTypings";
import { CorrectionPreviewModal } from "../../CorrectionPreviewModal";
import Modal, { ModalSize } from "../../../../misc/components/modal";
import { EntityLabelSelector, EntityLabelSelectorForm } from "./EntityLabelSelector";
import { useVisitScheduleCascades } from "../../../Hooks/UseVisitScheduleCascades";
import { VisibleWizards, Correction } from '../../../Request/DcfRequestReducer';
import { useWizard } from '../../../Hooks/UseWizard';
import { Form } from "informedv4";
import { CorrectionTypesEnum } from "../../../../misc/api/apiTypes";

export interface EntityLabelCorrectionWizardProps {
    studyId: string;
    siteId: string;
    isVisible: boolean;
    originalValue: any;
    subjectIdFieldDisplayText: string;
    onClose: () => void;
    onComplete: (corrections: Array<Correction>) => void;
    savedCorrections?: Correction[];
}

export interface EntityLabel {
    id: string;
    label: string;
}

export interface EntityLabelGroup {
    subject: EntityLabel;
    visit?: EntityLabel;
    session?: EntityLabel;
}

export const EntityLabelCorrectionWizard = ({
    studyId,
    siteId,
    isVisible,
    originalValue,
    subjectIdFieldDisplayText,
    savedCorrections,
    onClose,
    onComplete
}: EntityLabelCorrectionWizardProps) => {
    const { currentState, dispatch } = useWizard();

    const savedSubjectId = savedCorrections?.find(c => c.field === "SubjectId")?.newValue;
    const savedVisitId = savedCorrections?.find(c => c.field === "VisitScheduleId")?.newValue;
    const savedSessionId = savedCorrections?.find(c => c.field === "ScheduleItemId")?.newValue;

    const [selectedVisit, setSelectedVisit] = React.useState(savedVisitId || null);

    const [newLabel, setnewLabel] = React.useState(null);

    const { subjects, visits, sessions, loading } = useVisitScheduleCascades(studyId, originalValue ? originalValue.session.label : null, selectedVisit, originalValue.visit.id, siteId);

    if (!isVisible) {
        return null;
    }

    const handleSelectorNext = (form: EntityLabelSelectorForm) => {
        /* There is a bug in our version of informed which stops the onSubmit method from keeping in sync with
                        the state so we have to do the remapping outside of the form here, this has caused alot of the state
                        to be lifted to this component rather than staying in the EntityLabelSelector component */
        setnewLabel({
            subject: form.Subject,
            visit: form.Visit,
            session: form.Session
        });

        dispatch(WizardActionTypes.Next);
    };

    let page = null;
    switch (currentState.page) {
        case 0:
            page = (
                <Form
                    validateOn="submit"
                    initialValues={
                        {
                            Subject: savedSubjectId || originalValue.subject.id,
                            Visit: savedVisitId || null,
                            Session: savedSessionId || null,
                        }
                    }
                >
                    <EntityLabelSelector
                        subjectIdFieldDisplayText={subjectIdFieldDisplayText}
                        originalSubjectValue={originalValue.subject}
                        originalVisitValue={originalValue.visit}
                        originalSessionValue={originalValue.session}
                        possibleSubjects={subjects}
                        possibleVisits={visits}
                        possibleSessions={sessions}
                        loading={loading}
                        onVisitChange={id => setSelectedVisit(id)}
                        onNext={form => handleSelectorNext(form)}
                    />
                </Form>
            );
            break;
        case 1:
            const corrections = [
                { sourceCorrection: VisibleWizards.EntityLabels, correctionTypeId: CorrectionTypesEnum.MoveSession, entity: "Session", field: "SubjectId", oldValue: originalValue.subject.id, newValue: newLabel.subject, },
                { sourceCorrection: VisibleWizards.EntityLabels, correctionTypeId: CorrectionTypesEnum.MoveSession, entity: "Session", field: "VisitScheduleId", oldValue: originalValue.visit.id, newValue: newLabel.visit },
                { sourceCorrection: VisibleWizards.EntityLabels, correctionTypeId: CorrectionTypesEnum.MoveSession, entity: "Session", field: "ScheduleItemId", oldValue: originalValue.session.id, newValue: newLabel.session },
            ]

            page = (
                <CorrectionPreviewModal corrections={corrections}
                    miscCorrection={null}
                    onPrev={() => dispatch(WizardActionTypes.Previous)}
                    onNext={() => onComplete(corrections)}
                />
            );
            break;
        default:
            page = null;
    }

    return (
        <Modal
            show={true}
            header="Move Session"
            size={ModalSize.Large}
            closeModalHandler={onClose}
        >
            {page}
        </Modal>
    );
};
